import Slider from "@mui/material/Slider";
import { useEffect, useState } from "react";
import { CategoryType } from "../../../models/category-type.ts";
import { ProductDesign } from "../../../models/product-design.ts";
import { ProductFabric } from "../../../models/product-fabric.ts";
import { ProductType } from "../../../models/product-type.ts";
import { SizeOption } from "../../../models/size-option.ts";
import { getCategories } from "../../../Services/category.service.ts";
import {
  getProductsColors,
  getProductsPrices,
  getAllProductFabrics,
} from "../../../Services/product.service.ts";

export type FilterDismiss = {
  group: string;
  value: string;
  nonce: number;
};

const Filter = ({
  onFilterChange,
  resetFilters,
  initialCategory,
  dismiss,
}: {
  onFilterChange: (filters: any) => void;
  resetFilters: boolean;
  initialCategory: string;
  dismiss?: FilterDismiss | null;
}) => {
  const [value, setValue] = useState([0, 50000]);
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 50000 });
  const [availableColors, setAvailableColors] = useState<string[]>([]);
  const [availableCategories, setAvailableCategories] = useState<
    CategoryType[]
  >([]);

  const [selectedCategories, setSelectedCategories] = useState<CategoryType[]>(
    []
  );
  const [selectedProductTypes, setSelectedProductTypes] = useState<
    ProductType[]
  >([]);

  const [selectedProductDesigns, setSelectedProductDesigns] = useState<
    ProductDesign[]
  >([]);

  const [selectedProductFabrics, setSelectedProductFabrics] = useState<
    ProductFabric[]
  >([]);
  const [filterProductFabrics, setFilterProductFabrics] = useState<
    ProductFabric[]
  >([]);

  useEffect(() => {
    const filters = {
      categories: selectedCategories,
      productTypes: selectedProductTypes,
      sizes: selectedSizes,
      colors: selectedColors,
      designs: selectedProductDesigns,
      fabrics: selectedProductFabrics,
      priceRange: { min: value[0], max: value[1] },
    };
    onFilterChange(filters);
  }, [
    selectedCategories,
    selectedProductTypes,
    selectedSizes,
    selectedColors,
    selectedProductDesigns,
    selectedProductFabrics,
    value,
  ]);

  useEffect(() => {
    if (resetFilters) {
      setSelectedCategories([]);
      setSelectedProductTypes([]);
      setSelectedSizes([]);
      setSelectedColors([]);
      setSelectedProductDesigns([]);
      setSelectedProductFabrics([]);
      setValue([priceRange.min, priceRange.max]);
    }
  }, [resetFilters]);

  useEffect(() => {
    getAllColors();
    getAllCategories();
    getPriceRange().then((priceRange) => {
      if (priceRange) {
        setPriceRange(priceRange);
        setValue([priceRange.min, priceRange.max]);
      }
    });
    getAllProductFabrics().then((fabrics) => {
      if (Array.isArray(fabrics)) {
        setFilterProductFabrics(fabrics);
      }
    });
  }, []); // ← run once on mount

  useEffect(() => {
    if (!dismiss) return;
    const { group, value } = dismiss;
    if (group === "categories") {
      setSelectedCategories((prev) => prev.filter((item) => item !== value));
    } else if (group === "productTypes") {
      setSelectedProductTypes((prev) =>
        prev.filter((item) => item !== value)
      );
    } else if (group === "sizes") {
      setSelectedSizes((prev) => prev.filter((item) => item !== value));
    } else if (group === "colors") {
      setSelectedColors((prev) => prev.filter((item) => item !== value));
    } else if (group === "designs") {
      setSelectedProductDesigns((prev) =>
        prev.filter((item) => item !== value)
      );
    } else if (group === "fabrics") {
      setSelectedProductFabrics((prev) =>
        prev.filter((item) => item !== value)
      );
    }
  }, [dismiss]);

  useEffect(() => {
    if (initialCategory && availableCategories.length > 0) {
      const norm = (str) => str.trim().toLowerCase();
      const match = availableCategories.find(
        (cat) => norm(cat) === norm(initialCategory)
      );
      if (match && !selectedCategories.includes(match)) {
        setSelectedCategories([match]);
      }
    }
  }, [availableCategories, initialCategory]);

  const handleColorChange = (color) => {
    setSelectedColors((prevColors) =>
      prevColors.includes(color)
        ? prevColors.filter((c) => c !== color)
        : [...prevColors, color]
    );
  };

  const handleSizeChange = (size) => {
    setSelectedSizes((prevSizes) => {
      const newSizes = prevSizes.includes(size)
        ? prevSizes.filter((s) => s !== size)
        : [...prevSizes, size];
      return newSizes;
    });
  };

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleFilterChange = (event) => {
    const { name } = event.target;
    if (event.target.checked) {
      if (name.startsWith("category-")) {
        setSelectedCategories((prev) => [
          ...prev,
          event.target.value as CategoryType,
        ]);
      } else if (name.startsWith("productType-")) {
        setSelectedProductTypes((prev) => [
          ...prev,
          event.target.value as ProductType,
        ]);
      } else if (name.startsWith("productDesign-")) {
        setSelectedProductDesigns((prev) => [
          ...prev,
          event.target.value as ProductDesign,
        ]);
      } else if (name.startsWith("productFabric-")) {
        setSelectedProductFabrics((prev) => [
          ...prev,
          event.target.value as ProductFabric,
        ]);
      }
    } else {
      if (name.startsWith("category-")) {
        setSelectedCategories((prev) =>
          prev.filter((category) => category !== event.target.value)
        );
      } else if (name.startsWith("productType-")) {
        setSelectedProductTypes((prev) =>
          prev.filter((type) => type !== event.target.value)
        );
      } else if (name.startsWith("productDesign-")) {
        setSelectedProductDesigns((prev) =>
          prev.filter((design) => design !== event.target.value)
        );
      } else if (name.startsWith("productFabric-")) {
        setSelectedProductFabrics((prev) =>
          prev.filter((fabric) => fabric !== event.target.value)
        );
      }
    }
  };

  const getAllColors = async () => {
    try {
      const colors = await getProductsColors();
      if (colors.success) {
        const colorArray = (colors.data ?? []).map((c: String) => c.toString());
        setAvailableColors(colorArray);
      } else {
        console.error("Failed to fetch colors:", colors.error);
      }
    } catch (error) {
      console.error("Error fetching colors:", error);
    }
  };

  const getAllCategories = async () => {
    try {
      const categories = await getCategories();
      if (Array.isArray(categories)) {
        setAvailableCategories(categories.map((category) => category.name));
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const getPriceRange = async () => {
    try {
      const priceRange = await getProductsPrices();
      if (priceRange.success) {
        return priceRange.data;
      }
    } catch (error) {
      console.error("Error fetching price range:", error);
      return { min: 0, max: 1000 };
    }
  };

  const filterSizes = Object.values(SizeOption);
  const filterProductTypes = Object.values(ProductType);
  const filterProductDesigns = Object.values(ProductDesign);
  // filterProductFabrics is now fetched from API

  const Check = ({
    name,
    value,
    checked,
  }: {
    name: string;
    value: string;
    checked: boolean;
  }) => (
    <label className="opt">
      <input
        type="checkbox"
        name={name}
        value={value}
        checked={checked}
        onChange={handleFilterChange}
      />
      <span className="box" />
      {value}
    </label>
  );

  return (
    <>
      <details className="fgroup" open>
        <summary>Category</summary>
        <div className="opts">
          {availableCategories.map((category, index) => (
            <Check
              key={category}
              name={`category-${index}`}
              value={category}
              checked={selectedCategories.includes(category)}
            />
          ))}
        </div>
      </details>
      <details className="fgroup" open>
        <summary>Type</summary>
        <div className="opts">
          {filterProductTypes.map((type, index) => (
            <Check
              key={type}
              name={`productType-${index}`}
              value={type}
              checked={selectedProductTypes.includes(type as ProductType)}
            />
          ))}
        </div>
      </details>
      <details className="fgroup" open>
        <summary>Design</summary>
        <div className="opts">
          {filterProductDesigns.map((design, index) => (
            <Check
              key={design}
              name={`productDesign-${index}`}
              value={design}
              checked={selectedProductDesigns.includes(design as ProductDesign)}
            />
          ))}
        </div>
      </details>
      <details className="fgroup" open>
        <summary>Fabric</summary>
        <div className="opts">
          {filterProductFabrics.map((fabric, index) => (
            <Check
              key={fabric}
              name={`productFabric-${index}`}
              value={fabric}
              checked={selectedProductFabrics.includes(fabric as ProductFabric)}
            />
          ))}
        </div>
      </details>
      <details className="fgroup" open>
        <summary>Colour</summary>
        <div className="sw">
          {availableColors.map((color) => (
            <label key={color}>
              <input
                type="checkbox"
                checked={selectedColors.includes(color)}
                onChange={() => handleColorChange(color)}
              />
              <span className="c" style={{ background: color }} />
            </label>
          ))}
        </div>
      </details>
      <details className="fgroup" open>
        <summary>Size</summary>
        <div className="opts">
          {filterSizes.map((size) => (
            <label className="opt" key={size}>
              <input
                type="checkbox"
                checked={selectedSizes.includes(size as SizeOption)}
                onChange={() => handleSizeChange(size)}
              />
              <span className="box" />
              {size}
            </label>
          ))}
        </div>
      </details>
      <details className="fgroup" open>
        <summary>Price</summary>
        <Slider
          getAriaLabel={() => "Price range"}
          value={value}
          min={priceRange.min}
          max={priceRange.max}
          onChange={handleChange}
          getAriaValueText={(amount) => `Rs ${amount}`}
          valueLabelDisplay="auto"
          sx={{
            color: "#1a1a1a",
            "& .MuiSlider-thumb": {
              backgroundColor: "white",
              border: "2px solid #1a1a1a",
              width: 16,
              height: 16,
            },
          }}
        />
        <div className="price-readout">
          <span>Rs {value[0].toLocaleString()}</span>
          <span>Rs {value[1].toLocaleString()}</span>
        </div>
      </details>
    </>
  );
};

export default Filter;
